import PropTypes from 'prop-types';
import { Box } from '@mui/material';

/**
 * ActiveFilterChip — the small "<label> active" pill that names the widget
 * filter currently driving a table.
 *
 * Extracted from TableShell (ISS-037) so it can live in the table's filter
 * toolbar, to the left of the search box, instead of on its own line above
 * the card. Visual style is unchanged from the original TableShell chip;
 * only the margin is dropped since the toolbar row's own `gap` provides the
 * spacing between the chip and the search box.
 *
 * `aria-hidden` because the enclosing TableShell trace wrapper already
 * carries `aria-label="Table filtered by <label>"` — this component is the
 * visual half of that same signal, not an independent announcement.
 */
export default function ActiveFilterChip({ label, color, sx }) {
    if (!label || !color) {
        return null;
    }

    return (
        <Box
            aria-hidden
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                padding: '3px 10px',
                borderRadius: '10px',
                fontSize: '11px',
                fontWeight: 600,
                lineHeight: 1.6,
                color,
                background: `${color}14`,
                border: `1px solid ${color}40`,
                flexShrink: 0,
                whiteSpace: 'nowrap',
                alignSelf: 'center',
                ...sx,
            }}
        >
            <Box
                component="span"
                sx={{
                    width: '6px',
                    height: '6px',
                    borderRadius: '50%',
                    background: color,
                    flexShrink: 0,
                }}
            />
            {`${label} active`}
        </Box>
    );
}

ActiveFilterChip.propTypes = {
    // The active widget's label, e.g. 'Due Today'. Falsy → renders nothing.
    label: PropTypes.string,
    // The active widget's colour (hex), e.g. '#23a36d'. Falsy → renders nothing.
    color: PropTypes.string,
    // Extra sx merged onto the chip's root Box, for per-placement tweaks.
    sx: PropTypes.object,
};

ActiveFilterChip.defaultProps = {
    label: null,
    color: null,
    sx: {},
};
